import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	loadInfoNodes,
	reorderInfoNodes,
	deleteInfoNode,
} from "../../../actions";
import { ROOT_PARENT, normalizeNode } from "../utilities/dragUtils";

/** @typedef {import("../types").InfoNode} InfoNode */
/** @typedef {import("../types").InfoNodeItemsMap} InfoNodeItemsMap */
/** @typedef {import("../types").InfoNodeMap} InfoNodeMap */

/**
 * Handles the state of the tree.
 * Creates a local state (cache/optimistic update) to avoid re-rendering at each change.
 * Handles the sync with the server
 *
 * @param {String} site,
 */
export function useInfoNodesTree(site) {
	const dispatch = useDispatch();
	const rawNodes = useSelector((state) => state.infoPages.infoPages);
	const [isLoading, setIsLoading] = useState(true);
	const [optimisticState, setOptimisticState] = useState(null);

	const { normalizedNodes, nodesById, items } = useMemo(() => {
		const nodesById = new Map();
		const normalizedNodes = rawNodes
			.filter(Boolean)
			.sort((a, b) => a.order - b.order)
			.map((node) => {
				const normalized = normalizeNode(node);
				nodesById.set(normalized.RowId, normalized);
				return normalized;
			});

		const items = { [ROOT_PARENT]: [] };
		normalizedNodes.forEach((node) => {
			if (node.type === "folder") {
				items[node.RowId] = [];
			}
		});
		normalizedNodes.forEach((node) => {
			const parentId = node.parentId || ROOT_PARENT;
			const targetContainer = items[parentId] ? parentId : ROOT_PARENT;
			if (!items[targetContainer]) {
				items[targetContainer] = [];
			}
			items[targetContainer].push(node.RowId);
		});

		return { normalizedNodes, nodesById, items };
	}, [rawNodes]);

	const displayItems =
		optimisticState && optimisticState.items ? optimisticState.items : items;
	const displayNodesById =
		optimisticState && optimisticState.nodesById
			? optimisticState.nodesById
			: nodesById;
	const displayNormalizedNodes =
		optimisticState && optimisticState.normalizedNodes
			? optimisticState.normalizedNodes
			: normalizedNodes;

	const setOptimisticTree = useCallback((nextState) => {
		setOptimisticState({
			items: nextState.items,
			nodesById: nextState.nodesById,
			normalizedNodes: nextState.normalizedNodes,
		});
	}, []);

	const clearOptimisticTree = useCallback(() => {
		setOptimisticState(null);
	}, []);

	const handleLoadNodes = useCallback(
		(targetSite) => dispatch(loadInfoNodes(targetSite)),
		[dispatch],
	);

	const handleReorderNodes = useCallback(
		(targetSite, payload) => dispatch(reorderInfoNodes(targetSite, payload)),
		[dispatch],
	);

	const handleDeleteNode = useCallback(
		(payload) => dispatch(deleteInfoNode(payload)),
		[dispatch],
	);

	useEffect(() => {
		setOptimisticState(null);
	}, [rawNodes]);

	useEffect(() => {
		let isMounted = true;
		const hydrate = async () => {
			if (!site) {
				if (isMounted) setIsLoading(false);
				return;
			}

			if (isMounted) setIsLoading(true);
			try {
				await handleLoadNodes(site);
			} finally {
				if (isMounted) setIsLoading(false);
			}
		};

		hydrate();

		return () => {
			isMounted = false;
		};
	}, [site, handleLoadNodes]);

	return {
		displayItems,
		displayNodesById,
		displayNormalizedNodes,
		setOptimisticTree,
		clearOptimisticTree,
		handleReorderNodes,
		handleDeleteNode,
		isLoading,
	};
}
